/**
 * A STARTING POINT, NOT A CONSTRUCTION MANUAL (spec §62H).
 *
 * Renovation work has a broadly understood order — you do not hang drywall
 * before the rough-in passes inspection — but there is no single correct
 * sequence, and a program that pretends otherwise is worse than one that
 * offers nothing. So this library is:
 *
 *   • a suggestion, editable in every respect once it lands in a project;
 *   • scoped to work the project ACTUALLY HAS (spec §62I) — a house with no
 *     HVAC scope never gets an HVAC task;
 *   • explicit about where its numbers come from. A duration that arrived
 *     from here says so, and stops saying so the moment somebody changes it
 *     (spec §62J).
 *
 * The durations are illustrative for a small single-family flip. They are
 * not estimates of anybody's crew.
 *
 * Pure domain: no SQL, no React.
 */

import { isTradeId, type TradeId } from "./trade";

export type TaskCategory =
  | "planning"
  | "site"
  | "demolition"
  | "structure"
  | "exterior"
  | "mechanical"
  | "interior"
  | "finishes"
  | "fixtures"
  | "closeout";

export const TASK_CATEGORIES: ReadonlyArray<{ id: TaskCategory; displayName: string }> = [
  { id: "planning", displayName: "Permits & planning" },
  { id: "site", displayName: "Site" },
  { id: "demolition", displayName: "Demolition" },
  { id: "structure", displayName: "Structure" },
  { id: "exterior", displayName: "Exterior" },
  { id: "mechanical", displayName: "Mechanical rough-in" },
  { id: "interior", displayName: "Interior" },
  { id: "finishes", displayName: "Finishes" },
  { id: "fixtures", displayName: "Fixtures & appliances" },
  { id: "closeout", displayName: "Closeout" },
];

export interface TaskTemplate {
  id: string;
  name: string;
  category: TaskCategory;
  /** Working days. 0 = a milestone: a moment, not work. */
  durationDays: number;
  /** Template ids this one waits for, when both are in the schedule. */
  requires: readonly string[];
  /**
   * Feature definitions that put this task in a project's schedule. Empty
   * means "every renovation needs this" (permits, final clean).
   */
  scopeFeatureDefinitionIds?: readonly string[];
  /** Object definitions that put it there instead — spatial scope. */
  scopeObjectDefinitionIds?: readonly string[];
  notes?: string;
}

/**
 * The default library. Order here is the order tasks are created in, which
 * is also the order they read in a list — roughly the order of a job.
 */
export const TASK_TEMPLATES: readonly TaskTemplate[] = [
  {
    id: "permits",
    name: "Permits & approvals",
    category: "planning",
    durationDays: 10,
    requires: [],
    notes: "Lead time, not crew time.",
  },
  { id: "site-prep", name: "Site preparation", category: "site", durationDays: 1, requires: [] },
  {
    id: "demolition",
    name: "Demolition",
    category: "demolition",
    durationDays: 3,
    requires: ["site-prep"],
    scopeFeatureDefinitionIds: ["gut-room", "remove-interior-wall", "replace-cabinets", "replace-countertops", "replace-flooring"],
  },
  {
    id: "foundation",
    name: "Foundation & structural repair",
    category: "structure",
    durationDays: 5,
    requires: ["demolition"],
    scopeFeatureDefinitionIds: [
      "repair-slab-foundation",
      "repair-pier-and-beam",
      "level-foundation",
      "install-piers",
      "replace-beams",
      "repair-joists",
      "repair-subfloor-structure",
      "structural-repair",
    ],
  },
  {
    id: "framing",
    name: "Framing",
    category: "structure",
    durationDays: 4,
    requires: ["demolition", "foundation"],
    scopeFeatureDefinitionIds: ["construct-interior-wall", "remove-interior-wall"],
  },
  {
    id: "roofing",
    name: "Roofing",
    category: "exterior",
    durationDays: 3,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-roof", "repair-roof", "replace-roof-decking"],
  },
  {
    id: "windows",
    name: "Windows",
    category: "exterior",
    durationDays: 2,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-window"],
  },
  {
    id: "exterior-doors",
    name: "Exterior doors",
    category: "exterior",
    durationDays: 1,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-exterior-door"],
  },
  {
    id: "dry-in",
    name: "Dry-in",
    category: "exterior",
    durationDays: 0,
    requires: ["roofing", "windows", "exterior-doors"],
    scopeFeatureDefinitionIds: [
      "replace-roof",
      "repair-roof",
      "replace-roof-decking",
      "replace-window",
      "replace-exterior-door",
    ],
    notes: "Milestone: the building is closed to the weather.",
  },
  {
    id: "rough-plumbing",
    name: "Rough plumbing",
    category: "mechanical",
    durationDays: 4,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-toilet", "replace-vanity", "replace-tub", "replace-shower", "install-washer"],
    scopeObjectDefinitionIds: ["obj-toilet", "obj-vanity", "obj-tub", "obj-shower", "obj-kitchen-sink", "obj-washer"],
  },
  {
    id: "rough-electrical",
    name: "Rough electrical",
    category: "mechanical",
    durationDays: 4,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-light-fixture", "install-ceiling-fan", "install-dryer"],
    scopeObjectDefinitionIds: ["obj-light", "obj-ceiling-fan", "obj-outlet", "obj-switch", "obj-dryer"],
  },
  {
    id: "hvac",
    name: "HVAC rough-in",
    category: "mechanical",
    durationDays: 3,
    requires: ["framing"],
    scopeFeatureDefinitionIds: ["replace-hvac"],
  },
  {
    id: "rough-inspections",
    name: "Rough inspections",
    category: "mechanical",
    durationDays: 0,
    requires: ["rough-plumbing", "rough-electrical", "hvac", "dry-in"],
    scopeFeatureDefinitionIds: [
      "replace-toilet",
      "replace-vanity",
      "replace-tub",
      "replace-shower",
      "install-washer",
      "install-dryer",
      "replace-light-fixture",
      "install-ceiling-fan",
      "replace-hvac",
      "whole-house-repipe",
      "replace-electrical-panel",
    ],
    scopeObjectDefinitionIds: [
      "obj-toilet",
      "obj-vanity",
      "obj-tub",
      "obj-shower",
      "obj-kitchen-sink",
      "obj-washer",
      "obj-dryer",
      "obj-light",
      "obj-ceiling-fan",
      "obj-outlet",
      "obj-switch",
    ],
    notes: "Milestone: the rough-in work has passed.",
  },
  {
    id: "insulation",
    name: "Insulation",
    category: "interior",
    durationDays: 1,
    requires: ["rough-inspections"],
    scopeFeatureDefinitionIds: ["install-insulation"],
  },
  {
    id: "drywall",
    name: "Drywall",
    category: "interior",
    durationDays: 5,
    requires: ["insulation"],
    scopeFeatureDefinitionIds: ["repair-drywall", "install-insulation", "construct-interior-wall"],
  },
  {
    id: "interior-doors",
    name: "Interior doors",
    category: "interior",
    durationDays: 2,
    requires: ["drywall"],
    scopeFeatureDefinitionIds: ["replace-interior-door"],
  },
  {
    id: "cabinets",
    name: "Cabinets",
    category: "finishes",
    durationDays: 3,
    requires: ["drywall"],
    scopeFeatureDefinitionIds: ["replace-cabinets", "reface-cabinets", "replace-vanity"],
    scopeObjectDefinitionIds: [
      "obj-base-cabinet",
      "obj-drawer-base",
      "obj-sink-base",
      "obj-wall-cabinet",
      "obj-tall-cabinet",
      "obj-corner-base",
      "obj-corner-wall",
      "obj-bath-cabinet",
      "obj-base-cabinets",
      "obj-upper-cabinets",
      "obj-island-cabinets",
      "obj-vanity",
    ],
  },
  {
    id: "countertop-template",
    name: "Countertop template",
    category: "finishes",
    durationDays: 1,
    requires: ["cabinets"],
    scopeFeatureDefinitionIds: ["replace-countertops"],
    scopeObjectDefinitionIds: ["obj-countertop"],
    notes: "Measured on the installed cabinets.",
  },
  {
    id: "countertop-fabrication",
    name: "Countertop fabrication",
    category: "finishes",
    durationDays: 10,
    requires: ["countertop-template"],
    scopeFeatureDefinitionIds: ["replace-countertops"],
    scopeObjectDefinitionIds: ["obj-countertop"],
    notes: "Lead time at the fabricator, not crew time.",
  },
  {
    id: "flooring",
    name: "Flooring",
    category: "finishes",
    durationDays: 4,
    requires: ["drywall"],
    scopeFeatureDefinitionIds: ["replace-flooring", "refinish-hardwood"],
  },
  {
    id: "trim",
    name: "Trim & baseboards",
    category: "finishes",
    durationDays: 3,
    requires: ["interior-doors", "flooring"],
    scopeFeatureDefinitionIds: ["replace-baseboard", "install-crown-molding"],
  },
  {
    id: "painting",
    name: "Painting",
    category: "finishes",
    durationDays: 4,
    requires: ["trim"],
    scopeFeatureDefinitionIds: ["paint-walls", "paint-ceiling", "exterior-paint"],
  },
  {
    id: "countertop-install",
    name: "Countertop installation",
    category: "finishes",
    durationDays: 1,
    requires: ["countertop-fabrication", "painting"],
    scopeFeatureDefinitionIds: ["replace-countertops"],
    scopeObjectDefinitionIds: ["obj-countertop"],
  },
  {
    id: "backsplash",
    name: "Backsplash",
    category: "finishes",
    durationDays: 2,
    requires: ["countertop-install"],
    scopeFeatureDefinitionIds: ["install-backsplash"],
  },
  {
    id: "plumbing-fixtures",
    name: "Plumbing fixtures",
    category: "fixtures",
    durationDays: 2,
    requires: ["countertop-install", "painting"],
    scopeFeatureDefinitionIds: ["replace-toilet", "replace-kitchen-faucet", "replace-bathroom-faucet", "replace-kitchen-sink", "replace-tub", "replace-shower", "replace-vanity"],
    scopeObjectDefinitionIds: ["obj-toilet", "obj-tub", "obj-shower", "obj-kitchen-sink"],
  },
  {
    id: "electrical-fixtures",
    name: "Electrical fixtures",
    category: "fixtures",
    durationDays: 2,
    requires: ["painting"],
    scopeFeatureDefinitionIds: ["replace-light-fixture", "install-ceiling-fan"],
    scopeObjectDefinitionIds: ["obj-light", "obj-ceiling-fan", "obj-outlet", "obj-switch"],
  },
  {
    id: "appliances",
    name: "Appliances",
    category: "fixtures",
    durationDays: 1,
    requires: ["electrical-fixtures", "plumbing-fixtures"],
    scopeFeatureDefinitionIds: ["install-washer", "install-dryer"],
    scopeObjectDefinitionIds: ["obj-range", "obj-refrigerator", "obj-dishwasher", "obj-washer", "obj-dryer", "obj-laundry-stacked"],
  },
  {
    id: "punch-list",
    name: "Punch list",
    category: "closeout",
    durationDays: 3,
    requires: ["appliances", "backsplash", "trim"],
  },
  { id: "final-clean", name: "Final clean", category: "closeout", durationDays: 1, requires: ["punch-list"] },
  {
    id: "listing-ready",
    name: "Listing ready",
    category: "closeout",
    durationDays: 0,
    requires: ["final-clean"],
    notes: "Milestone: the house can be photographed and listed.",
  },
];

export function templateById(id: string): TaskTemplate | null {
  return TASK_TEMPLATES.find((t) => t.id === id) ?? null;
}

/** Keep declared sequencing through omitted suggestions without inventing tasks. */
export function selectedTemplatePrerequisites(templateId: string, available: ReadonlySet<string>): string[] {
  const found = new Set<string>();
  const visited = new Set<string>([templateId]);
  const visit = (id: string) => {
    if (visited.has(id)) return;
    visited.add(id);
    if (available.has(id)) { found.add(id); return; }
    for (const prerequisite of templateById(id)?.requires ?? []) visit(prerequisite);
  };
  for (const prerequisite of templateById(templateId)?.requires ?? []) visit(prerequisite);
  return [...found];
}

/**
 * WHICH BUDGET CATEGORY EACH SEEDED TASK SPENDS AGAINST (spec §75D).
 *
 * A task's own `category` is a SCHEDULE PHASE ("mechanical", "finishes") —
 * it answers when in the job this happens. This table answers the money
 * question instead: the §74 trade category whose budget this task's work
 * draws down. Declared deliberately, one line per template, because a
 * guess from a task's text is exactly what this must never be.
 *
 * A template MISSING from this table has no financial category, and that
 * is a legitimate answer: permits, inspections and the final walkthrough
 * are real scheduled work with no renovation budget behind them.
 *
 * Judgement calls worth stating:
 *  • DEMOLITION is the tear-out task whatever is being torn out (§74D).
 *  • Rough-in and fixture tasks of one trade share that trade's ONE
 *    budget — rough plumbing and plumbing fixtures both spend Plumbing.
 *  • The three countertop tasks (template, fabricate, install) are all
 *    Countertops: one budget, three moments in the schedule.
 *  • Framing is Framing & Carpentry (structural); trim is Cabinetry &
 *    Finish Carpentry — different scopes, usually different crews (§74F).
 */
export const TEMPLATE_BUDGET_CATEGORY: Readonly<Record<string, TradeId>> = {
  demolition: "demolition",
  foundation: "foundation-structural",
  framing: "carpentry",
  roofing: "roofing",
  "dry-in": "roofing",
  windows: "doors-windows",
  "exterior-doors": "doors-windows",
  "interior-doors": "doors-windows",
  "rough-plumbing": "plumbing",
  "plumbing-fixtures": "plumbing",
  "rough-electrical": "electrical",
  "electrical-fixtures": "electrical",
  hvac: "hvac",
  insulation: "insulation",
  drywall: "drywall",
  cabinets: "cabinetry",
  trim: "cabinetry",
  "countertop-template": "countertops",
  "countertop-fabrication": "countertops",
  "countertop-install": "countertops",
  flooring: "flooring",
  backsplash: "tile",
  painting: "painting-interior",
  appliances: "appliance-install",
  "final-clean": "cleaning",
  // Deliberately absent (no budget behind them): permits, site-prep,
  // rough-inspections, punch-list, listing-ready.
};

/**
 * The budget category a scheduled task spends against, in precedence
 * order — and NEVER inferred from the task's name (spec §75D):
 *
 *   1. what somebody explicitly said this task represents;
 *   2. the §74 category of the feature the task is linked to;
 *   3. the category its seeded template declares;
 *   4. nothing — an honest "this task costs no renovation money".
 */
export function taskBudgetCategory(task: {
  budgetCategory?: string | null;
  featureCategory?: TradeId | null;
  templateId?: string | null;
}): TradeId | null {
  if (task.budgetCategory && isTradeId(task.budgetCategory)) return task.budgetCategory;
  if (task.featureCategory) return task.featureCategory;
  if (task.templateId) return TEMPLATE_BUDGET_CATEGORY[task.templateId] ?? null;
  return null;
}

export function categoryLabel(id: string): string {
  return TASK_CATEGORIES.find((c) => c.id === id)?.displayName ?? "Other";
}

/**
 * Which templates belong in THIS project's schedule, given the scope it
 * actually has. A template with no scope requirement is always relevant
 * (permits, clean-up); one with a requirement appears only if the project
 * has matching work; and any template another chosen one waits on is pulled
 * in too, so the sequence stays connected rather than dangling.
 */
export function templatesForScope(scope: {
  featureDefinitionIds: ReadonlySet<string>;
  objectDefinitionIds: ReadonlySet<string>;
}): TaskTemplate[] {
  const chosen = new Set<string>();

  const matches = (template: TaskTemplate): boolean => {
    const features = template.scopeFeatureDefinitionIds ?? [];
    const objects = template.scopeObjectDefinitionIds ?? [];
    if (features.length === 0 && objects.length === 0) return true;
    return (
      features.some((id) => scope.featureDefinitionIds.has(id)) ||
      objects.some((id) => scope.objectDefinitionIds.has(id))
    );
  };

  for (const template of TASK_TEMPLATES) {
    if (matches(template)) chosen.add(template.id);
  }

  // A prerequisite the project does NOT have is simply absent, and the
  // dependency on it is never created (spec §62I). Pulling in a whole trade
  // because something else nominally follows it would put HVAC in the
  // schedule of a house with no HVAC work — which is exactly what this is
  // here to prevent.
  return TASK_TEMPLATES.filter((t) => chosen.has(t.id));
}

/** These phases need an explicit scope/jurisdiction decision, not fixture counts. */
export const OPTIONAL_TASK_PHASES = ["permits", "site-prep", "rough-plumbing", "rough-electrical", "rough-inspections", "listing-ready"] as const;
export function recommendedTemplatesForScope(scope: {
  featureDefinitionIds: ReadonlySet<string>;
  objectDefinitionIds: ReadonlySet<string>;
}): TaskTemplate[] {
  const selected = new Set(templatesForScope(scope)
    .filter(t => !OPTIONAL_TASK_PHASES.some(id => id === t.id)).map(t => t.id));
  if (scope.featureDefinitionIds.has("whole-house-repipe")) selected.add("rough-plumbing");
  if (scope.featureDefinitionIds.has("replace-electrical-panel")) selected.add("rough-electrical");
  return TASK_TEMPLATES.filter(t => selected.has(t.id));
}
